import { DataLabelMode } from '../../core/Chart.types';
import { RING_RADIUS_FACTOR, SELECTED_RADIUS_FACTOR } from './PieSlice.utils';
import {
  computePolylineScale,
  LINE_HORIZONTAL_LENGTH,
  LINE_RADIAL_LENGTH,
  POLYLINE_HIDE_RADIUS,
  POLYLINE_LABEL_WIDTH,
} from './polyline.utils';
import { TAG_LABEL_INITIAL_WIDTH } from './tagLabel.utils';

const MAX_RADIUS_FACTOR = RING_RADIUS_FACTOR + SELECTED_RADIUS_FACTOR;
const POLYLINE_VERTICAL_PADDING = 36;
const FULL_POLYLINE_OFFSET = LINE_RADIAL_LENGTH + LINE_HORIZONTAL_LENGTH + POLYLINE_LABEL_WIDTH;
const TAG_LABEL_CENTER_OFFSET = 4;

type Margins = { top: number; bottom: number; left: number; right: number };

const getDataLabelsOffset = (dataLabelMode: DataLabelMode | undefined, scale: number): { x: number; y: number } => {
  if (!dataLabelMode) return { x: 0, y: 0 };
  if (dataLabelMode === 'polyline') {
    return {
      x: FULL_POLYLINE_OFFSET * scale,
      y: POLYLINE_VERTICAL_PADDING * scale,
    };
  }
  return { x: TAG_LABEL_INITIAL_WIDTH, y: TAG_LABEL_INITIAL_WIDTH };
};

const computeRadius = (width: number, height: number, margins: Margins, offset: { x: number; y: number }) => {
  const innerWidth = width * (1 - MAX_RADIUS_FACTOR) - margins.left - margins.right - offset.x * 2;
  const innerHeight = height * (1 - MAX_RADIUS_FACTOR) - margins.top - margins.bottom - offset.y * 2;
  return Math.max(0, Math.min(innerWidth, innerHeight) / 2);
};

const resolvePolylineLayout = (width: number, height: number, margins: Margins) => {
  const r1 = computeRadius(width, height, margins, getDataLabelsOffset('polyline', 1));
  const scale = computePolylineScale(r1);
  const scaled = getDataLabelsOffset('polyline', scale);
  const r2 = computeRadius(width, height, margins, scaled);
  if (r2 < POLYLINE_HIDE_RADIUS) return { offset: getDataLabelsOffset(undefined, 1), polylineScale: 1 };
  return { offset: scaled, polylineScale: scale };
};

export const computeSizeAndPosition = (
  width: number,
  height: number,
  marginTop: number,
  marginBottom: number,
  marginLeft: number,
  marginRight: number,
  dataLabelMode?: DataLabelMode
) => {
  const margins: Margins = { top: marginTop, bottom: marginBottom, left: marginLeft, right: marginRight };
  const isPolyline = dataLabelMode === 'polyline';

  const { offset, polylineScale } = isPolyline
    ? resolvePolylineLayout(width, height, margins)
    : { offset: getDataLabelsOffset(dataLabelMode, 1), polylineScale: 1 };

  const effectiveDataLabelMode = isPolyline && offset.x === 0 ? undefined : dataLabelMode;

  const innerWidth = width * (1 - MAX_RADIUS_FACTOR) - margins.left - margins.right - offset.x * 2;
  const innerHeight = height * (1 - MAX_RADIUS_FACTOR) - margins.top - margins.bottom - offset.y * 2;
  const radius = Math.max(0, Math.min(innerWidth, innerHeight) / 2);
  const centerY = (innerHeight / 2) * (1 + MAX_RADIUS_FACTOR) + offset.y;
  const centerX = (innerWidth / 2) * (1 + MAX_RADIUS_FACTOR) + offset.x + (dataLabelMode === 'tag' ? TAG_LABEL_CENTER_OFFSET : 0);

  return {
    centerX,
    centerY,
    radius,
    effectiveDataLabelMode,
    polylineScale,
  };
};
